import type { CSSProperties } from "react";

/**
 * Column layouts for the Brugerstyring tables.
 *
 * The tables render with `table-fixed`, so the browser takes each column's
 * width from the header row instead of measuring the widest cell. Under the
 * default auto layout the widths were re-measured on every render, so the
 * headers jumped sideways whenever a sort swapped the rows, whenever the
 * skeleton took over during a refetch, and whenever a page happened to hold a
 * longer value (LAR-399).
 *
 * Widths are numbers rather than Tailwind classes for two reasons: Tailwind's
 * JIT only emits utilities it can see as literal strings, so a computed
 * `w-[${n}px]` would silently produce no CSS; and numbers let the tests check
 * the arithmetic that actually matters — that the flexible column still has
 * room once the pinned columns have taken their share.
 *
 * Every `px` and `minWidth` below was measured from the rendered header and a
 * worst-case row, so a pinned column always fits its own contents.
 */

export type ColumnWidth =
  /** A share of the table width; grows with the window. */
  | { readonly percent: number }
  /** A pinned width, for fixed-size controls and fixed-format values. */
  | { readonly px: number }
  /** Absorbs whatever the pinned columns leave over. Exactly one per table. */
  | "flex";

export interface TableLayout<TColumn extends string> {
  /**
   * Narrowest the table may render, in px. Below this the pinned columns would
   * eat the flexible column, so the table keeps this width and overflows its
   * container instead — the same thing the old auto layout did, minus the drift.
   */
  readonly minWidth: number;
  readonly columns: Readonly<Record<TColumn, ColumnWidth>>;
}

const defineLayout = <TColumn extends string>(
  layout: TableLayout<TColumn>
): TableLayout<TColumn> => layout;

/** Inline width for a header cell. The flexible column is left unset. */
export const columnStyle = (width: ColumnWidth): CSSProperties => {
  if (width === "flex") return {};
  return "px" in width ? { width: width.px } : { width: `${width.percent}%` };
};

/** What the flexible column resolves to once the pinned columns are served. */
export const flexibleColumnWidth = (
  layout: TableLayout<string>,
  tableWidth: number
): number => {
  const pinned = Object.values<ColumnWidth>(layout.columns).reduce(
    (total, width) => {
      if (width === "flex") return total;
      return (
        total + ("px" in width ? width.px : (width.percent / 100) * tableWidth)
      );
    },
    0
  );

  return tableWidth - pinned;
};

/** The bulk-select checkbox column, identical wherever bulk mode exists. */
const SELECT_COLUMN: ColumnWidth = { px: 48 };

/** "Brugere" tab. Measured natural widths: 42 / 278 / 244 / 258 = 822. */
export const EXISTING_USERS_LAYOUT = defineLayout({
  minWidth: 840,
  columns: {
    select: SELECT_COLUMN,
    name: { percent: 30 },
    email: { percent: 26 },
    roles: "flex",
  },
});

/**
 * "Inviterede brugere" tab. Measured natural widths:
 * 237 / 204 / 127 / 161 / 182 / 119 / 120 = 1150.
 *
 * Seven columns with long Danish headers genuinely need this much room, so
 * everything except the email column is pinned at its measured width and the
 * table carries a min-width. Percentages here would starve the email column on
 * a narrow window, which is the tab's primary value.
 */
export const INVITED_USERS_LAYOUT = defineLayout({
  minWidth: 1160,
  columns: {
    email: "flex",
    inviter: { px: 205 },
    status: { px: 135 },
    createdAt: { px: 165 },
    expiresAt: { px: 185 },
    roles: { px: 125 },
    actions: { px: 125 },
  },
});

/** "Grupper" tab. Measured natural widths: 42 / 175 / 159 / 138 = 514. */
export const GROUPS_LAYOUT = defineLayout({
  minWidth: 600,
  columns: {
    select: SELECT_COLUMN,
    name: "flex",
    memberCount: { px: 170 },
    actions: { px: 150 },
  },
});

/**
 * "Forældreløse workspaces" tab. Measured natural widths:
 * 42 / 138 / 344 / 106 / 113 / 122 = 865.
 */
export const ORPHANED_WORKSPACES_LAYOUT = defineLayout({
  minWidth: 900,
  columns: {
    select: SELECT_COLUMN,
    name: { percent: 22 },
    description: "flex",
    documentCount: { px: 115 },
    createdAt: { px: 130 },
    actions: { px: 140 },
  },
});
